/* eslint-disable @typescript-eslint/no-explicit-any */

'use client'
import { Input } from "@/components/ui/input";
import { FileText, Loader2, Phone, ShoppingCart, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAxios } from "@/hooks";
import endPoint from "@/utils/endPoint.util";
import SubmitPopup from "./components/submitPopup/submitPopup.component";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { usePathname } from "next/navigation";

interface TProps {
    className?: string;
}


const validatePhone = (phone: string) => /^\+?\d{7,15}$/.test(phone);

const ContactUsForm = ({ className }: TProps) => {
    const path =  usePathname();
    const [showSubmitPopup, setShowSubmitPopup] = useState(false);

    const { fetchData, loading } = useAxios();

    const [form, setForm] = useState({
        name: "",
        mobile: "",
        shopName: "",
        message: "",
    });
    
    const [errors, setErrors] = useState({
        name: "",
        mobile: "",
        shopName: "",
        message: "",
    });
    
    const handleClose = () => {
        setShowSubmitPopup(false);
        setForm({
            name: "",
            mobile: "",
            shopName: "",
            message: "",
        });
    };

    const validate = () => {
        let valid = true;
        const newErrors = { name: "", email: "", mobile: "", shopName: '', message: "" };

        if (!form.name.trim()) {
            newErrors.name = "Name is required";
            valid = false;
        }
        if (!form.mobile.trim()) {
            newErrors.mobile = "Phone number is required";
            valid = false;
        } else if (!validatePhone(form.mobile)) {
            newErrors.mobile = "Invalid phone number";
            valid = false;
        }
        if (!form.shopName.trim()) {
            newErrors.shopName = "Shop name is required";
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
        setErrors((prev) => ({
            ...prev,
            [name]: "",
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;
        const data = await fetchData({
            url: endPoint.POST_CONTACT_FORM,
            method: "POST",
            data: {
                name: form.name,
                mobile: form.mobile,
                shopName: form.shopName,
                message: form.message,
                from: 'FYDO_WEBSITE',
                pageUrl: path.replace("/", ""),
            },
        });

        if (data) {
            setShowSubmitPopup(true);
            if (typeof window !== 'undefined' && (window as any).gtag) {
                (window as any).gtag('event', 'conversion', { 'send_to': 'AW-10856486693/egj_CLfE_rADEKWu47go' });
            }
        }
    };

    return (
        <>
            <div className={cn("bg-white md:p-8 p-4 md:w-1/2 rounded-lg", className)}>
                <form
                    className="flex flex-col gap-6"
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <div className="relative ">
                        <label htmlFor="contact-name" className="sr-only">Your Name</label>
                        <Input
                            id="contact-name"
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="Your Name*"
                            className={`!bg-transparent text-sm placeholder:text-stone-500 text-black border-b border-t-0 border-x-0 border-stone-300 rounded-none pl-8  ${errors.name ? "border-red-500" : ""
                                }`}
                            aria-invalid={!!errors.name}
                            aria-describedby="name-error"
                        />
                        <User className="absolute text-stone-400 left-0 top-1/2 -translate-y-1/2" />
                        {errors.name && (
                            <span
                                id="name-error"
                                className="text-xs text-red-500 absolute left-0 -bottom-5"
                            >
                                {errors.name}
                            </span>
                        )}
                    </div>
                    {/* <div className="relative ">
                        <Input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            placeholder="Email Address"
                            className={`!bg-transparent text-sm placeholder:text-stone-500 border-b text-black border-t-0 border-x-0 border-stone-300 rounded-none pl-8  ${errors.email ? "border-red-500" : ""
                                }`}
                            aria-invalid={!!errors.email}
                            aria-describedby="email-error"
                        />
                        <Mail className="absolute text-stone-400 left-0 top-1/2 -translate-y-1/2" />
                        {errors.email && (
                            <span
                                id="email-error"
                                className="text-xs text-red-500 absolute left-0 -bottom-5"
                            >
                                {errors.email}
                            </span>
                        )}
                    </div> */}
                    <div className="relative ">
                        <label htmlFor="contact-mobile" className="sr-only">Phone Number</label>
                        <Input
                            id="contact-mobile"
                            type="number"
                            name="mobile"
                            value={form.mobile}
                            onChange={handleChange}
                            placeholder="Phone Number*"
                            className={`!bg-transparent text-sm placeholder:text-stone-500 border-b text-black border-t-0 border-x-0 border-stone-300 rounded-none pl-8  ${errors.mobile ? "border-red-500" : ""
                                }`}
                            aria-invalid={!!errors.mobile}
                            aria-describedby="mobile-error"
                        />
                        <Phone className="absolute text-stone-400 left-0 top-1/2 -translate-y-1/2" />
                        {errors.mobile && (
                            <span
                                id="mobile-error"
                                className="text-xs text-red-500 absolute left-0 -bottom-5"
                            >
                                {errors.mobile}
                            </span>
                        )}
                    </div>
                    <div className="relative ">
                        <label htmlFor="contact-shopName" className="sr-only">Shop Name</label>
                        <Input
                            id="contact-shopName"
                            type="text"
                            name="shopName"
                            value={form.shopName}
                            onChange={handleChange}
                            placeholder="Shop Name*"
                            className={`!bg-transparent text-sm placeholder:text-stone-500 border-b text-black border-t-0 border-x-0 border-stone-300 rounded-none pl-8  ${errors.shopName ? "border-red-500" : ""
                                }`}
                            aria-invalid={!!errors.shopName}
                            aria-describedby="shopName-error"
                        />
                        <ShoppingCart className="absolute text-stone-400 left-0 top-1/2 -translate-y-1/2" />
                        {errors.shopName && (
                            <span
                                id="shopName-error"
                                className="text-xs text-red-500 absolute left-0 -bottom-5"
                            >
                                {errors.shopName}
                            </span>
                        )}
                    </div>
                    <div className="relative ">
                        <label htmlFor="contact-message" className="sr-only">Message</label>
                        <Input
                            id="contact-message"
                            type="text"
                            name="message"
                            value={form.message}
                            onChange={handleChange}
                            placeholder="Message"
                            className={`!bg-transparent text-sm placeholder:text-stone-500 border-b text-black border-t-0 border-x-0 border-stone-300 rounded-none pl-8  ${errors.message ? "border-red-500" : ""
                                }`}
                            aria-invalid={!!errors.message}
                            aria-describedby="message-error"
                        />
                        <FileText className="absolute text-stone-400 left-0 top-1/2 -translate-y-1/2" />
                    </div>
                    <Button variant="default" className="w-full mt-4" type="submit" disabled={loading}>
                        {loading ? <Loader2 className="animate-spin" /> : "Get a Free Demo"}
                    </Button>
                </form>
            </div>
            {showSubmitPopup && <SubmitPopup onClose={handleClose} />}
        </>
    );
}

export default ContactUsForm;